import React, { useState, useEffect, useCallback } from 'react';
import {
  ChartBarIcon,
  UsersIcon,
  UserGroupIcon,
  MegaphoneIcon,
  ArrowPathIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { reportsApi, audienceMemberApi, communicationApi } from '../../api';
import { toast } from 'react-toastify';
import MemberCommunicationModal from './MemberCommunicationModal';

const REPORT_TYPES = {
  members: { label: 'Audience Members', icon: UsersIcon },
  audiences: { label: 'Audiences', icon: UserGroupIcon },
  campaigns: { label: 'Campaigns', icon: MegaphoneIcon },
};

const MEMBER_TYPES = [
  { value: null, label: 'All Types' },
  { value: 0, label: 'Auto Shop' },
  { value: 1, label: 'ITP Station' },
];

const INTEREST_LEVELS = [
  { value: null, label: 'All Interest' },
  { value: 0, label: 'Undefined' },
  { value: 1, label: 'Not Interested' },
  { value: 2, label: 'Interested' },
  { value: 3, label: 'Very Interested' },
];

const STATUS_LEVELS = [
  { value: null, label: 'All Status' },
  { value: 0, label: 'Not Contacted' },
  { value: 1, label: 'Contacted' },
  { value: 2, label: 'Not answered' },
  { value: 3, label: 'Requires Action' },
  { value: 4, label: 'Paying User' },
];

const INTEREST_API_VALUES = ['UNDEFINED', 'NOT_INTERESTED', 'INTERESTED', 'VERY_INTERESTED'];
const STATUS_API_VALUES = ['NOT_CONTACTED', 'CONTACTED', 'WAITING_MEMBER_COMM', 'REQUIRES_ACTION', 'PAYING_USER'];
const TYPE_API_VALUES = ['AUTO_SHOP', 'ITP_STATION'];

const COMMUNICATION_TYPE_META = {
  NOTE: { label: 'Internal Note', icon: DocumentTextIcon, color: 'bg-gray-100 text-gray-800' },
  CALL: { label: 'Phone Call', icon: PhoneIcon, color: 'bg-blue-100 text-blue-800' },
  EMAIL: { label: 'Email', icon: EnvelopeIcon, color: 'bg-green-100 text-green-800' },
  WHATSAPP: { label: 'WhatsApp', icon: ChatBubbleLeftRightIcon, color: 'bg-emerald-100 text-emerald-800' },
  SMS: { label: 'SMS', icon: ChatBubbleLeftRightIcon, color: 'bg-purple-100 text-purple-800' },
  MEETING: { label: 'Meeting', icon: UserGroupIcon, color: 'bg-orange-100 text-orange-800' },
};

const SORT_OPTIONS = [
  { value: 'created_at', label: 'Created Date' },
  { value: 'name', label: 'Name' },
  { value: 'type', label: 'Type' },
  { value: 'interest', label: 'Interest' },
  { value: 'status', label: 'CRM Status' },
  { value: 'followUpDate', label: 'Follow-up Date' },
  { value: 'opens', label: 'Opens' },
  { value: 'turnover', label: 'Turnover' },
];

const getTypeStyle = (type) => {
  return type === 'ITP_STATION' || type === 1
    ? { label: 'ITP Station', color: 'bg-blue-100 text-blue-800' }
    : { label: 'Auto Shop', color: 'bg-purple-100 text-purple-800' };
};

const getInterestStyle = (interest) => {
  switch (interest) {
    case 'VERY_INTERESTED':
    case 3:
      return { label: 'Very Interested', color: 'bg-green-100 text-green-800' };
    case 'INTERESTED':
    case 2:
      return { label: 'Interested', color: 'bg-yellow-100 text-yellow-800' };
    case 'NOT_INTERESTED':
    case 1:
      return { label: 'Not Interested', color: 'bg-red-100 text-red-800' };
    default:
      return { label: 'Undefined', color: 'bg-gray-100 text-gray-600' };
  }
};

const getStatusStyle = (status) => {
  switch (status) {
    case 'PAYING_USER':
    case 4:
      return { label: 'Paying User', color: 'bg-green-100 text-green-800' };
    case 'REQUIRES_ACTION':
    case 3:
      return { label: 'Requires Action', color: 'bg-red-100 text-red-800' };
    case 'WAITING_MEMBER_COMM':
    case 2:
      return { label: 'Not answered', color: 'bg-yellow-100 text-yellow-800' };
    case 'CONTACTED':
    case 1:
      return { label: 'Contacted', color: 'bg-blue-100 text-blue-800' };
    default:
      return { label: 'Not Contacted', color: 'bg-gray-100 text-gray-600' };
  }
};

const getCommunicationTypeMeta = (type) => {
  return COMMUNICATION_TYPE_META[type] || COMMUNICATION_TYPE_META.NOTE;
};

const normalizeRomanianPhoneForWhatsApp = (input) => {
  if (!input) {
    return { isValid: false, normalizedE164: null, waDigits: null, reason: 'Missing phone number' };
  }

  const compact = String(input).trim().replace(/[\s().-]/g, '');
  if (!compact) {
    return { isValid: false, normalizedE164: null, waDigits: null, reason: 'Missing phone number' };
  }

  let normalizedInput = compact;
  if (normalizedInput.startsWith('00')) {
    normalizedInput = `+${normalizedInput.slice(2)}`;
  }

  if (!/^\+?\d+$/.test(normalizedInput)) {
    return { isValid: false, normalizedE164: null, waDigits: null, reason: 'Invalid phone format' };
  }

  const asPlus40 = (nationalMobile) => {
    const e164 = `+40${nationalMobile}`;
    return { isValid: true, normalizedE164: e164, waDigits: e164.slice(1), reason: null };
  };

  if (normalizedInput.startsWith('+')) {
    if (!normalizedInput.startsWith('+40')) {
      return { isValid: false, normalizedE164: null, waDigits: null, reason: 'Phone must be Romanian (+40)' };
    }
    const national = normalizedInput.slice(3);
    if (/^7\d{8}$/.test(national)) {
      return asPlus40(national);
    }
    return { isValid: false, normalizedE164: null, waDigits: null, reason: 'Romanian mobile must have 9 digits after +40' };
  }

  if (/^07\d{8}$/.test(normalizedInput)) {
    return asPlus40(normalizedInput.slice(1));
  }

  if (/^7\d{8}$/.test(normalizedInput)) {
    return asPlus40(normalizedInput);
  }

  if (/^407\d{8}$/.test(normalizedInput)) {
    const e164 = `+${normalizedInput}`;
    return { isValid: true, normalizedE164: e164, waDigits: normalizedInput, reason: null };
  }

  if (/^40\d{9}$/.test(normalizedInput)) {
    const national = normalizedInput.slice(2);
    if (/^7\d{8}$/.test(national)) {
      const e164 = `+${normalizedInput}`;
      return { isValid: true, normalizedE164: e164, waDigits: normalizedInput, reason: null };
    }
  }

  return { isValid: false, normalizedE164: null, waDigits: null, reason: 'Unsupported Romanian phone number' };
};

const MarketingReports = () => {
  const [reportType, setReportType] = useState('members');
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedMemberId, setExpandedMemberId] = useState(null);
  const pageSize = 20;

  // Member report filters
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState(null);
  const [interestFilter, setInterestFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [currentSoftFilter, setCurrentSoftFilter] = useState('');
  const [softwareOptions, setSoftwareOptions] = useState([]);
  const [minTurnover, setMinTurnover] = useState('');
  const [maxTurnover, setMaxTurnover] = useState('');
  const [debouncedMinTurnover, setDebouncedMinTurnover] = useState('');
  const [debouncedMaxTurnover, setDebouncedMaxTurnover] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortAsc, setSortAsc] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Modals
  const [showCommunicationModal, setShowCommunicationModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  // Inline editing + row detail data
  const [editingInterestMemberId, setEditingInterestMemberId] = useState(null);
  const [editingStatusMemberId, setEditingStatusMemberId] = useState(null);
  const [savingMemberId, setSavingMemberId] = useState(null);
  const [memberCommunications, setMemberCommunications] = useState({});
  const [memberFinancialReports, setMemberFinancialReports] = useState({});
  const [memberCommunicationTotals, setMemberCommunicationTotals] = useState({});
  const [loadingCommunications, setLoadingCommunications] = useState({});
  const [loadingFinancialReports, setLoadingFinancialReports] = useState({});

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Debounce turnover filters
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedMinTurnover(minTurnover);
      setPage(1);
    }, 600);
    return () => clearTimeout(timer);
  }, [minTurnover]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedMaxTurnover(maxTurnover);
      setPage(1);
    }, 600);
    return () => clearTimeout(timer);
  }, [maxTurnover]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      switch (reportType) {
        case 'members':
          response = await reportsApi.getMemberReportEnhanced(page, pageSize, {
            query: debouncedSearch || null,
            type: typeFilter,
            interest: interestFilter,
            status: statusFilter,
            currentSoft: currentSoftFilter || null,
            minTurnover: debouncedMinTurnover !== '' ? debouncedMinTurnover : null,
            maxTurnover: debouncedMaxTurnover !== '' ? debouncedMaxTurnover : null,
            sortBy,
            sortAsc,
          });
          break;
        case 'audiences':
          response = await reportsApi.getAudienceReport(page, pageSize, sortAsc);
          break;
        case 'campaigns':
          response = await reportsApi.getCampaignReport(page, pageSize, sortAsc);
          break;
        default:
          response = { items: [], total: 0 };
      }
      setData(response);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  }, [reportType, page, debouncedSearch, typeFilter, interestFilter, statusFilter, currentSoftFilter, debouncedMinTurnover, debouncedMaxTurnover, sortBy, sortAsc]);

  useEffect(() => {
    const loadSoftwareOptions = async () => {
      if (reportType !== 'members') return;
      try {
        const values = await audienceMemberApi.getDistinctSoftware();
        setSoftwareOptions(Array.isArray(values) ? values : []);
      } catch (error) {
        console.error('Error loading software options:', error);
        setSoftwareOptions([]);
      }
    };

    loadSoftwareOptions();
  }, [reportType]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    setPage(1);
    setExpandedRow(null);
    setExpandedMemberId(null);
    setEditingInterestMemberId(null);
    setEditingStatusMemberId(null);
  }, [reportType]);

  useEffect(() => {
    if (reportType !== 'members' || !data.items?.length) return;
    const memberIds = data.items.map((item) => item.memberId).filter(Boolean);
    if (memberIds.length === 0) return;

    const fetchCommunicationTotals = async () => {
      try {
        const counts = await Promise.all(
          memberIds.map(async (memberId) => {
            const resp = await communicationApi.getByMemberId(memberId, 1, 1);
            return [memberId, resp?.total || 0];
          })
        );
        setMemberCommunicationTotals(Object.fromEntries(counts));
      } catch (error) {
        console.error('Error fetching communication totals:', error);
      }
    };

    fetchCommunicationTotals();
  }, [reportType, data.items]);

  const toggleExpandRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('ro-RO').format(value);
  };

  const getLatestCommunicationTitle = (memberId) => {
    const communications = memberCommunications[memberId] || [];
    if (!communications.length) {
      return 'Latest communication: No communications yet';
    }

    const latest = communications.reduce((currentLatest, communication) => {
      const currentDate = new Date(communication.communicationDate || 0).getTime();
      const latestDate = new Date(currentLatest.communicationDate || 0).getTime();
      return currentDate > latestDate ? communication : currentLatest;
    }, communications[0]);

    const latestType = getCommunicationTypeMeta(latest.communicationType).label;
    return `Latest communication: ${latestType} (${formatDate(latest.communicationDate)})`;
  };

  const loadMemberCommunications = async (memberId) => {
    if (!memberId) return;
    setLoadingCommunications((prev) => ({ ...prev, [memberId]: true }));
    try {
      const response = await communicationApi.getByMemberId(memberId, 1, 10);
      setMemberCommunications((prev) => ({ ...prev, [memberId]: response?.communications || [] }));
    } catch (error) {
      console.error('Error fetching communications:', error);
      toast.error('Failed to load communications');
    } finally {
      setLoadingCommunications((prev) => ({ ...prev, [memberId]: false }));
    }
  };

  const loadMemberFinancialReports = async (memberId, refresh = false) => {
    if (!memberId) return;
    setLoadingFinancialReports((prev) => ({ ...prev, [memberId]: true }));
    try {
      const response = await audienceMemberApi.getFinancialReports(memberId, 5, refresh);
      setMemberFinancialReports((prev) => ({
        ...prev,
        [memberId]: Array.isArray(response) ? response : [],
      }));
    } catch (error) {
      console.error('Error fetching financial reports:', error);
      toast.error('Failed to load financial reports');
    } finally {
      setLoadingFinancialReports((prev) => ({ ...prev, [memberId]: false }));
    }
  };

  const handleViewMemberDetails = async (member) => {
    const memberId = member.memberId;
    const nextId = expandedMemberId === memberId ? null : memberId;
    setExpandedMemberId(nextId);
    if (!nextId) return;

    const loaders = [];
    if (!memberCommunications[memberId]) {
      loaders.push(loadMemberCommunications(memberId));
    }
    if (!memberFinancialReports[memberId]) {
      loaders.push(loadMemberFinancialReports(memberId));
    }

    if (loaders.length > 0) {
      await Promise.all(loaders);
    }
  };

  const handleRefreshFinancialReports = async (memberId) => {
    await loadMemberFinancialReports(memberId, true);
    toast.success('Financial reports refreshed from ANAF');
  };

  const mapMemberEnumToOrdinal = (value, allowedValues) => {
    if (typeof value === 'number') return value;
    const idx = allowedValues.indexOf(value);
    return idx >= 0 ? idx : 0;
  };

  const handleInlineMemberUpdate = async (member, updates) => {
    try {
      setSavingMemberId(member.memberId);
      const currentInterest = mapMemberEnumToOrdinal(member.interest, INTEREST_API_VALUES);
      const currentStatus = mapMemberEnumToOrdinal(member.status, STATUS_API_VALUES);

      const nextInterest = updates.interest !== undefined ? updates.interest : currentInterest;
      const nextStatus = updates.status !== undefined ? updates.status : currentStatus;
      await audienceMemberApi.update({
        id: member.memberId,
        memberInfo: {
          name: member.name,
          email: member.email,
          phone: member.phone,
          cui: member.cui,
        },
        type: typeof member.type === 'string' ? member.type : TYPE_API_VALUES[member.type] || 'AUTO_SHOP',
        interest: INTEREST_API_VALUES[nextInterest] || 'UNDEFINED',
        status: STATUS_API_VALUES[nextStatus] || 'NOT_CONTACTED',
      });
      toast.success('Member updated successfully');
      setEditingInterestMemberId(null);
      setEditingStatusMemberId(null);

      setData((prev) => {
        const nextItems = (prev.items || []).map((item) => {
          if (item.memberId !== member.memberId) return item;
          return {
            ...item,
            interest: updates.interest !== undefined ? INTEREST_API_VALUES[updates.interest] || item.interest : item.interest,
            status: updates.status !== undefined ? STATUS_API_VALUES[updates.status] || item.status : item.status,
          };
        });
        return { ...prev, items: nextItems };
      });
    } catch (error) {
      console.error('Error updating member:', error);
      toast.error('Failed to update member');
    } finally {
      setSavingMemberId(null);
    }
  };

  const handleMemberUpdated = () => {
    fetchReport();
  };

  const handleToggleFollowUp = async (memberId, communication) => {
    try {
      await communicationApi.markFollowUpCompleted(communication.id);
      toast.success(
        communication.followUpCompleted ? 'Follow-up marked as pending' : 'Follow-up marked as completed'
      );
      await loadMemberCommunications(memberId);
    } catch (error) {
      console.error('Error updating follow-up status:', error);
      toast.error('Failed to update follow-up status');
    }
  };

  const handleDeleteCommunication = async (memberId, communicationId) => {
    if (!window.confirm('Are you sure you want to delete this communication?')) return;
    try {
      await communicationApi.delete(communicationId);
      toast.success('Communication deleted');
      await loadMemberCommunications(memberId);
      fetchReport();
    } catch (error) {
      console.error('Error deleting communication:', error);
      toast.error('Failed to delete communication');
    }
  };

  const handleOpenWhatsApp = (phoneNumber) => {
    const normalized = normalizeRomanianPhoneForWhatsApp(phoneNumber);
    if (!normalized.isValid) {
      toast.error(`Cannot open WhatsApp: ${normalized.reason}`);
      return;
    }

    const internalWhatsappUrl = `${window.location.origin}/whatsapp/${normalized.waDigits}`;
    window.open(internalWhatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const exportToCsv = () => {
    let csvContent = '';
    let filename = '';

    if (reportType === 'members') {
      csvContent = 'Member ID,Name,Email,Phone,CUI,Type,Current Software,Interest,Status,Follow-up Date,Total Opens\n';
      data.items.forEach((item) => {
        csvContent += `"${item.memberId}","${item.name || ''}","${item.email || ''}","${item.phone || ''}","${item.cui || ''}","${getTypeStyle(item.type).label}","${item.currentSoft || ''}","${getInterestStyle(item.interest).label}","${getStatusStyle(item.status).label}","${item.nextFollowUpDate ? new Date(item.nextFollowUpDate).toLocaleDateString() : ''}",${item.totalOpens || 0}\n`;
      });
      filename = 'member_report.csv';
    } else if (reportType === 'audiences') {
      csvContent = 'Audience ID,Audience Name,Member Count,Total Opens\n';
      data.items.forEach((item) => {
        csvContent += `"${item.audienceId}","${item.audienceName || ''}",${item.memberCount},${item.totalOpens}\n`;
      });
      filename = 'audience_report.csv';
    } else if (reportType === 'campaigns') {
      csvContent = 'Campaign ID,Campaign Name,Status,Total Emails,Sent,Opened,Failed,Open Rate,Delivery Rate,Created At\n';
      data.items.forEach((item) => {
        csvContent += `"${item.campaignId}","${item.campaignName || ''}","${item.status}",${item.totalEmails},${item.sent},${item.opened},${item.failed},${item.openRate?.toFixed(2) || 0}%,${item.deliveryRate?.toFixed(2) || 0}%,"${item.createdAtUtc || ''}"\n`;
      });
      filename = 'campaign_report.csv';
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    toast.success('Report exported successfully');
  };

  const renderMembersTable = () => (
    <table className="min-w-full divide-y divide-gray-300">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Software</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Interest</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">CRM Status</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Follow-up</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opens</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
          {data.items.length === 0 ? (
          <tr>
            <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
              No data found
            </td>
          </tr>
        ) : (
          data.items.map((item, index) => {
            const typeStyle = getTypeStyle(item.type);
            const interestStyle = getInterestStyle(item.interest);
            const statusStyle = getStatusStyle(item.status);
            const whatsappPhone = normalizeRomanianPhoneForWhatsApp(item.phone);

            return (
              <React.Fragment key={item.memberId}>
                <tr className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100`}>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{item.name || 'Unknown'}</div>
                    {(item.cui || item.lastKnownTurnover != null) && (
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        {item.cui && <span>CUI: {item.cui}</span>}
                        {item.cui && item.lastKnownTurnover != null && <span>·</span>}
                        {item.lastKnownTurnover != null && (
                          <span>
                            {formatNumber(item.lastKnownTurnover)} RON
                            {item.lastKnownYear && <span className="ml-1">({item.lastKnownYear})</span>}
                          </span>
                        )}
                      </div>
                    )}
                    {item.currentSoft && (
                      <div className="text-xs text-gray-500 mt-0.5">Software: {item.currentSoft}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-500">{item.email || '-'}</div>
                    {item.phone && <div className="text-xs text-gray-400">{item.phone}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${typeStyle.color}`}>
                      {typeStyle.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {item.currentSoft || <span className="text-gray-400">N/A</span>}
                  </td>
                  <td className="px-4 py-3">
                    {editingInterestMemberId === item.memberId ? (
                      <select
                        autoFocus
                        defaultValue={mapMemberEnumToOrdinal(item.interest, INTEREST_API_VALUES)}
                        disabled={savingMemberId === item.memberId}
                        onBlur={() => setEditingInterestMemberId(null)}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10);
                          setEditingInterestMemberId(null);
                          handleInlineMemberUpdate(item, { interest: value });
                        }}
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                      >
                        {INTEREST_LEVELS.slice(1).map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setEditingInterestMemberId(item.memberId)}
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${interestStyle.color} hover:opacity-80`}
                        title="Click to update interest"
                      >
                        {interestStyle.label}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {editingStatusMemberId === item.memberId ? (
                      <select
                        autoFocus
                        defaultValue={mapMemberEnumToOrdinal(item.status, STATUS_API_VALUES)}
                        disabled={savingMemberId === item.memberId}
                        onBlur={() => setEditingStatusMemberId(null)}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10);
                          setEditingStatusMemberId(null);
                          handleInlineMemberUpdate(item, { status: value });
                        }}
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                      >
                        {STATUS_LEVELS.slice(1).map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setEditingStatusMemberId(item.memberId)}
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle.color} hover:opacity-80`}
                        title="Click to update CRM status"
                      >
                        {statusStyle.label}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {item.nextFollowUpDate ? (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        new Date(item.nextFollowUpDate) < new Date()
                          ? 'bg-red-100 text-red-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {new Date(item.nextFollowUpDate).toLocaleDateString('ro-RO')}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      item.totalOpens > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {item.totalOpens || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-start gap-4">
                      <div className="flex flex-col items-center">
                        <button
                          type="button"
                          onClick={() => handleViewMemberDetails(item)}
                          className="relative inline-flex h-7 w-7 items-center justify-center rounded-full text-purple-600 hover:bg-purple-50 hover:text-purple-800"
                          title="View Campaigns"
                        >
                          <span className="absolute -right-2 -top-2 min-w-[18px] rounded-full border border-purple-200 bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium leading-none text-purple-700">
                            {item.campaignDetails?.length || 0}
                          </span>
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="flex flex-col items-center">
                        <button
                          type="button"
                          onClick={() => handleViewMemberDetails(item)}
                          className="relative inline-flex h-7 w-7 items-center justify-center rounded-full text-blue-600 hover:bg-blue-50 hover:text-blue-800"
                          title={getLatestCommunicationTitle(item.memberId)}
                        >
                          <span className="absolute -right-2 -top-2 min-w-[18px] rounded-full border border-blue-200 bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium leading-none text-blue-700">
                            {memberCommunicationTotals[item.memberId] ?? 0}
                          </span>
                          <ChatBubbleLeftRightIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleOpenWhatsApp(item.phone)}
                        className={`inline-flex h-7 w-7 items-center justify-center rounded-full border transition-colors ${
                          whatsappPhone.isValid
                            ? 'border-emerald-200 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-800'
                            : 'border-gray-200 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                        }`}
                        title={
                          whatsappPhone.isValid
                            ? `Send WhatsApp to ${whatsappPhone.normalizedE164}`
                            : `WhatsApp unavailable: ${whatsappPhone.reason}`
                        }
                      >
                        <PhoneIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleViewMemberDetails(item)}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
                        title={expandedMemberId === item.memberId ? 'Hide details' : 'Show details'}
                      >
                        {expandedMemberId === item.memberId ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedMemberId === item.memberId && (
                  <tr>
                    <td colSpan={9} className="bg-gray-50 px-4 py-4 align-top">
                      <div className="grid gap-4 lg:grid-cols-3">
                        <div className="rounded-md border border-emerald-100 bg-white p-3 text-left">
                          <div className="mb-2 flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900">Financial Reports</p>
                            <button
                              type="button"
                              onClick={() => handleRefreshFinancialReports(item.memberId)}
                              disabled={Boolean(loadingFinancialReports[item.memberId])}
                              className="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                              title="Refresh from ANAF"
                            >
                              <ArrowPathIcon className={`mr-1 h-3.5 w-3.5 ${loadingFinancialReports[item.memberId] ? 'animate-spin' : ''}`} />
                              Refresh
                            </button>
                          </div>
                          <div className="h-80 overflow-y-auto pr-1">
                            {loadingFinancialReports[item.memberId] ? (
                              <p className="text-sm text-gray-500">Loading financial reports...</p>
                            ) : memberFinancialReports[item.memberId]?.length ? (
                              <div className="space-y-2">
                                {memberFinancialReports[item.memberId].map((report) => (
                                  <div key={report.year} className="rounded border border-emerald-200 bg-emerald-50 p-2">
                                    <p className="text-sm font-semibold text-emerald-800">Year {report.year}</p>
                                    <div className="mt-1 space-y-1 text-xs text-gray-700">
                                      <p>Cifra afaceri: {formatNumber(report.cifraAfaceri)}</p>
                                      <p>Profit net: {formatNumber(report.profitNet)}</p>
                                      <p>Datorii totale: {formatNumber(report.datoriiTotale)}</p>
                                      <p>Active imobilizate: {formatNumber(report.activeImobilizate)}</p>
                                      <p>Active circulante: {formatNumber(report.activeCirculante)}</p>
                                      <p>Capitaluri proprii: {formatNumber(report.capitaluriProprii)}</p>
                                      <p>Numar salariati: {formatNumber(report.numarSalariati)}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">No financial reports found</p>
                            )}
                          </div>
                        </div>

                        <div className="rounded-md border border-purple-100 bg-white p-3 text-left">
                          <p className="mb-2 text-sm font-medium text-gray-900">Campaigns</p>
                          <div className="h-80 overflow-y-auto pr-1">
                            {item.campaignDetails?.length ? (
                              <div className="space-y-2">
                                {item.campaignDetails.map((campaign) => (
                                  <div key={campaign.campaignId} className="rounded border border-purple-200 bg-purple-50 p-2">
                                    <p className="text-sm font-medium text-gray-700">{campaign.campaignName}</p>
                                    <p className="text-xs text-gray-500">
                                      {campaign.openCount} {campaign.openCount === 1 ? 'open' : 'opens'}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">No campaigns for this member</p>
                            )}
                          </div>
                        </div>

                        <div className="rounded-md border border-blue-100 bg-white p-3 text-left">
                          <div className="mb-2 flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900">Communications</p>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedMember({
                                  id: item.memberId,
                                  memberInfo: { name: item.name, email: item.email, phone: item.phone },
                                  status: item.status,
                                  currentSoft: item.currentSoft || '',
                                  openAddForm: true,
                                });
                                setShowCommunicationModal(true);
                              }}
                              className="inline-flex items-center rounded-md bg-purple-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-purple-700"
                            >
                              <PlusIcon className="mr-1 h-3.5 w-3.5" />
                              Add New
                            </button>
                          </div>

                          <div className="h-80 overflow-y-auto pr-1">
                            {loadingCommunications[item.memberId] ? (
                              <p className="text-sm text-gray-500">Loading communications...</p>
                            ) : memberCommunications[item.memberId]?.length ? (
                              <div className="space-y-2">
                                {memberCommunications[item.memberId].map((comm) => {
                                  const typeMeta = getCommunicationTypeMeta(comm.communicationType);
                                  const TypeIcon = typeMeta.icon;
                                  const isInbound = comm.direction === 'INBOUND';

                                  return (
                                    <div
                                      key={comm.id}
                                      className="rounded-lg border border-gray-200 bg-white p-4 text-left transition-shadow hover:shadow-sm"
                                    >
                                      <div className="flex items-start justify-between">
                                        <div className="flex min-w-0 items-start gap-3">
                                          <div className={`rounded-lg p-2 ${typeMeta.color}`}>
                                            <TypeIcon className="h-5 w-5" />
                                          </div>
                                          <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeMeta.color}`}>
                                                {typeMeta.label}
                                              </span>
                                              {comm.communicationType !== 'NOTE' && (
                                                <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${
                                                  isInbound ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                  {isInbound ? '← Inbound' : '→ Outbound'}
                                                </span>
                                              )}
                                              {comm.followUpDate && (
                                                <div className="inline-flex items-center gap-2">
                                                  {comm.followUpCompleted ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                      <CheckCircleIcon className="h-3 w-3 mr-1" />
                                                      Follow-up Completed
                                                    </span>
                                                  ) : (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                                      <ClockIcon className="h-3 w-3 mr-1" />
                                                      Follow-up: {formatDate(comm.followUpDate)}
                                                    </span>
                                                  )}

                                                  <button
                                                    type="button"
                                                    onClick={() => handleToggleFollowUp(item.memberId, comm)}
                                                    className={`text-xs px-2 py-1 rounded transition-colors border ${
                                                      comm.followUpCompleted
                                                        ? 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-gray-300'
                                                        : 'bg-green-50 text-green-700 hover:bg-green-100 border-green-200'
                                                    }`}
                                                    title={comm.followUpCompleted ? 'Revert to pending' : 'Mark follow-up as completed'}
                                                  >
                                                    {comm.followUpCompleted ? 'Revert' : 'Mark Done'}
                                                  </button>
                                                </div>
                                              )}
                                            </div>

                                            {comm.subject && (
                                              <p className="mt-1 text-sm font-medium text-gray-900">{comm.subject}</p>
                                            )}
                                            <p className="mt-1 whitespace-pre-wrap break-words text-left text-sm text-gray-600">{comm.content}</p>
                                            <p className="mt-2 text-xs text-gray-400">{formatDate(comm.communicationDate)}</p>
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => handleDeleteCommunication(item.memberId, comm.id)}
                                          className="text-gray-400 transition-colors hover:text-red-600"
                                          title="Delete"
                                        >
                                          <TrashIcon className="h-5 w-5" />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">No communications yet</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })
        )}
      </tbody>
    </table>
  );

  const renderAudiencesTable = () => (
    <table className="min-w-full divide-y divide-gray-300">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Audience Name</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Members</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Opens</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {data.items.length === 0 ? (
          <tr>
            <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
              No data found
            </td>
          </tr>
        ) : (
          data.items.map((item, index) => (
            <React.Fragment key={item.audienceId}>
              <tr className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100`}>
                <td className="px-4 py-3 text-sm text-gray-900">{item.audienceName || 'Unknown'}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{item.memberCount}</td>
                <td className="px-4 py-3 text-sm">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    item.totalOpens > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {item.totalOpens}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">
                  {item.campaignDetails?.length > 0 && (
                    <button
                      onClick={() => toggleExpandRow(item.audienceId)}
                      className="text-purple-600 hover:text-purple-800 flex items-center gap-1"
                    >
                      <EyeIcon className="h-4 w-4" />
                      <span>Campaigns ({item.campaignDetails.length})</span>
                      {expandedRow === item.audienceId ? (
                        <ChevronUpIcon className="h-4 w-4" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </td>
              </tr>
              {expandedRow === item.audienceId && item.campaignDetails?.length > 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-3 bg-purple-50">
                    <div className="text-sm">
                      <p className="font-medium text-gray-900 mb-2">Campaigns that generated opens:</p>
                      <div className="space-y-1">
                        {item.campaignDetails.map((campaign) => (
                          <div key={campaign.campaignId} className="flex items-center justify-between bg-white p-2 rounded">
                            <span className="text-gray-700">{campaign.campaignName}</span>
                            <span className="text-gray-500">
                              {campaign.openCount} {campaign.openCount === 1 ? 'open' : 'opens'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))
        )}
      </tbody>
    </table>
  );

  const renderCampaignsTable = () => (
    <table className="min-w-full divide-y divide-gray-300">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opened</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Failed</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Open Rate</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {data.items.length === 0 ? (
          <tr>
            <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
              No data found
            </td>
          </tr>
        ) : (
          data.items.map((item, index) => (
            <tr key={item.campaignId} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100`}>
              <td className="px-4 py-3 text-sm text-gray-900">{item.campaignName || 'Unknown'}</td>
              <td className="px-4 py-3 text-sm">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  item.status === 'COMPLETED' ? 'bg-purple-100 text-purple-800' :
                  item.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                  item.status === 'PAUSED' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {item.status}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-gray-500">{item.sent}</td>
              <td className="px-4 py-3 text-sm">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  item.opened > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {item.opened}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-red-500">{item.failed}</td>
              <td className="px-4 py-3 text-sm text-gray-500">{item.openRate?.toFixed(1) || 0}%</td>
              <td className="px-4 py-3 text-sm text-gray-500">
                {item.createdAtUtc ? new Date(item.createdAtUtc).toLocaleDateString() : '-'}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2">
            <ChartBarIcon className="h-6 w-6 text-purple-600" />
            <h2 className="text-lg font-medium text-gray-900">Marketing Reports</h2>
          </div>

          <div className="flex items-center gap-4">
            {/* Report Type Selector */}
            <div className="flex rounded-md shadow-sm">
              {Object.entries(REPORT_TYPES).map(([key, { label, icon: Icon }]) => (
                <button
                  key={key}
                  onClick={() => setReportType(key)}
                  className={`
                    px-4 py-2 text-sm font-medium flex items-center gap-2
                    ${key === 'members' ? 'rounded-l-md' : ''}
                    ${key === 'campaigns' ? 'rounded-r-md' : ''}
                    ${reportType === key
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                    }
                    ${key !== 'members' && reportType !== key ? '-ml-px' : ''}
                  `}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>

            {/* Export Button */}
            <button
              onClick={exportToCsv}
              disabled={data.items.length === 0}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Member Report Filters */}
        {reportType === 'members' && (
          <div className="bg-gray-50 rounded-lg p-4 space-y-4">
            {/* Search and Filter Toggle */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  placeholder="Search by name, email, phone, or CUI..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium ${
                  showFilters ? 'bg-purple-100 text-purple-700 border-purple-300' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filters
                {(typeFilter !== null || interestFilter !== null || statusFilter !== null || minTurnover !== '' || maxTurnover !== '') && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-600 text-white">
                    {[typeFilter !== null, interestFilter !== null, statusFilter !== null, currentSoftFilter !== '', minTurnover !== '', maxTurnover !== ''].filter(Boolean).length}
                  </span>
                )}
              </button>
            </div>

            {/* Filter Controls */}
            {showFilters && (
              <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={typeFilter ?? ''}
                    onChange={(e) => { setTypeFilter(e.target.value === '' ? null : parseInt(e.target.value)); setPage(1); }}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  >
                    {MEMBER_TYPES.map((opt) => (
                      <option key={opt.label} value={opt.value ?? ''}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Interest</label>
                  <select
                    value={interestFilter ?? ''}
                    onChange={(e) => { setInterestFilter(e.target.value === '' ? null : parseInt(e.target.value)); setPage(1); }}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  >
                    {INTEREST_LEVELS.map((opt) => (
                      <option key={opt.label} value={opt.value ?? ''}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">CRM Status</label>
                  <select
                    value={statusFilter ?? ''}
                    onChange={(e) => { setStatusFilter(e.target.value === '' ? null : parseInt(e.target.value)); setPage(1); }}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  >
                    {STATUS_LEVELS.map((opt) => (
                      <option key={opt.label} value={opt.value ?? ''}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Sort By</label>
                  <div className="flex gap-2">
                    <select
                      value={sortBy}
                      onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                      className="block flex-1 border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    >
                      {SORT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => setSortAsc(!sortAsc)}
                      className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm hover:bg-gray-50"
                      title={sortAsc ? 'Ascending' : 'Descending'}
                    >
                      {sortAsc ? '↑' : '↓'}
                    </button>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Current Software</label>
                  <input
                    type="text"
                    list="marketing-reports-software-options"
                    value={currentSoftFilter}
                    onChange={(e) => { setCurrentSoftFilter(e.target.value); setPage(1); }}
                    placeholder="ServiceM"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  />
                  <datalist id="marketing-reports-software-options">
                    {softwareOptions.map((software) => (
                      <option key={software} value={software} />
                    ))}
                  </datalist>
                </div>
              </div>
              {/* Turnover filters */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Min Turnover (RON)</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="e.g. 100000"
                    value={minTurnover}
                    onChange={(e) => setMinTurnover(e.target.value)}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Max Turnover (RON)</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="e.g. 5000000"
                    value={maxTurnover}
                    onChange={(e) => setMaxTurnover(e.target.value)}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  />
                </div>
              </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Loading report...</p>
            </div>
          ) : (
            <>
              {reportType === 'members' && renderMembersTable()}
              {reportType === 'audiences' && renderAudiencesTable()}
              {reportType === 'campaigns' && renderCampaignsTable()}
            </>
          )}
        </div>
      </div>

      {/* Pagination */}
      {data.items.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{data.items.length}</span> of{' '}
            <span className="font-medium">{data.total}</span> items
            (Page {page})
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page * pageSize >= data.total}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Communication Modal */}
      <MemberCommunicationModal
        show={showCommunicationModal}
        member={selectedMember}
        onClose={() => {
          setShowCommunicationModal(false);
          if (selectedMember?.id) {
            loadMemberCommunications(selectedMember.id);
          }
          setSelectedMember(null);
        }}
        onMemberUpdated={handleMemberUpdated}
      />
    </div>
  );
};

export default MarketingReports;

